import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo } from "react";
import {
  PAGE_SIZE,
  PRICE_MAX,
  PRICE_MIN,
  filterAndSort,
  type CatalogFilters,
  type SortValue,
} from "@/lib/catalog/products";
import { getCatalogRepository } from "@/lib/catalog/repository";
import { CatalogToolbar } from "@/components/catalog/CatalogToolbar";
import { FilterPanel } from "@/components/catalog/FilterPanel";
import { CatalogGrid } from "@/components/catalog/CatalogGrid";
import { EmptyState, ProductGridSkeleton } from "@/components/catalog/CatalogStates";
import { canonicalUrl } from "@/lib/seo/site";
import { breadcrumbSchema, itemListSchema, ldScript } from "@/lib/seo/schema";


const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  family: fallback(z.string().array(), []).default([]),
  gender: fallback(z.string().array(), []).default([]),
  concentration: fallback(z.string().array(), []).default([]),
  collection: fallback(z.string(), "").default(""),
  min: fallback(z.number(), PRICE_MIN).default(PRICE_MIN),
  max: fallback(z.number(), PRICE_MAX).default(PRICE_MAX),
  sort: fallback(z.string(), "featured").default("featured"),
  page: fallback(z.number(), 1).default(1),
});

type Search = {
  q: string;
  family: string[];
  gender: string[];
  concentration: string[];
  collection: string;
  min: number;
  max: number;
  sort: string;
  page: number;
};

export const Route = createFileRoute("/fragrances/")({
  validateSearch: zodValidator(searchSchema),
  loader: () => getCatalogRepository().getProducts(),
  pendingComponent: () => <ProductGridSkeleton />,
  head: ({ loaderData }) => ({
    meta: [
      { title: "Fragrances — Shop the CB16 Maison Collection" },
      {
        name: "description",
        content:
          "Browse the full CB16 fragrance catalog. Filter by family, gender, concentration, and price. Hand-blended in small batches at the CB16 atelier in Mumbai, India.",
      },
      { property: "og:title", content: "Fragrances — CB16 Maison" },
      {
        property: "og:description",
        content:
          "The full CB16 fragrance catalog — filter, sort, and search hand-composed perfumes.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl("/fragrances") },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Fragrances — CB16 Maison" },
      {
        name: "twitter:description",
        content:
          "The full CB16 fragrance catalog — filter, sort, and search hand-composed perfumes.",
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/fragrances") }],
    scripts: [
      ldScript(
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Fragrances", path: "/fragrances" },
        ]),
      ),
      ...(loaderData?.length ? [ldScript(itemListSchema(loaderData))] : []),
    ],
  }),

  component: FragrancesPage,
});

function FragrancesPage() {
  const search = Route.useSearch();
  const products = Route.useLoaderData();
  const navigate = useNavigate({ from: Route.fullPath });

  const filters: CatalogFilters = useMemo(
    () => ({
      q: search.q,
      families: search.family,
      genders: search.gender,
      concentrations: search.concentration,
      collection: search.collection,
      min: search.min,
      max: search.max,
      sort: (search.sort as SortValue) ?? "featured",
    }),
    [search],
  );

  const results = useMemo(() => filterAndSort(products, filters), [products, filters]);
  const visibleCount = Math.min(results.length, search.page * PAGE_SIZE);
  const visible = results.slice(0, visibleCount);
  const canLoadMore = visibleCount < results.length;

  function applyPatch(patch: Partial<CatalogFilters>) {
    navigate({
      search: (prev: Search) => ({
        ...prev,
        q: patch.q ?? prev.q,
        family: patch.families ?? prev.family,
        gender: patch.genders ?? prev.gender,
        concentration: patch.concentrations ?? prev.concentration,
        collection: patch.collection ?? prev.collection,
        min: patch.min ?? prev.min,
        max: patch.max ?? prev.max,
        sort: patch.sort ?? prev.sort,
        page: 1,
      }),
      replace: true,
    });
  }

  function reset() {
    navigate({
      search: {
        q: "",
        family: [],
        gender: [],
        concentration: [],
        collection: "",
        min: PRICE_MIN,
        max: PRICE_MAX,
        sort: "featured",
        page: 1,
      },
      replace: true,
    });
  }

  function loadMore() {
    navigate({ search: (prev: Search) => ({ ...prev, page: prev.page + 1 }), replace: true });
  }

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
            The Catalog
          </span>
          <h1
            className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Fragrances
          </h1>
          <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-black/60 sm:text-base">
            Every flacon in the CB16 maison, hand-composed and numbered. Refine by family, gender,
            or price to find your signature.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-12">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <FilterPanel filters={filters} onChange={applyPatch} onReset={reset} />
            </div>
          </aside>

          <div className="min-w-0">
            <CatalogToolbar
              filters={filters}
              totalCount={results.length}
              onChange={applyPatch}
              onReset={reset}
            />

            <div className="mt-10">
              {results.length === 0 && <EmptyState onReset={reset} />}
              {results.length > 0 && (
                <>
                  <CatalogGrid products={visible} />
                  {canLoadMore && (
                    <div className="mt-16 flex justify-center">
                      <button
                        type="button"
                        onClick={loadMore}
                        className="border border-black px-8 py-3 text-[11px] font-medium uppercase tracking-[0.28em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
                      >
                        Load More ({results.length - visibleCount} remaining)
                      </button>
                    </div>
                  )}
                  {!canLoadMore && results.length > PAGE_SIZE && (
                    <p className="mt-16 text-center text-[11px] uppercase tracking-[0.28em] text-black/40">
                      End of collection
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
